import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { Mail } from 'lucide-react';
import { clearAuthMessages } from '../../../store/actions/AuthActions';
import { authPageLink } from '../../../utils/OnboardingRedirect';
import { requestEmailVerification, verificationFailureMessage, verificationNotice } from '../../../services/EmailVerificationService';

export default function EmailVerificationNotice({ redirectUrl }) {
  const pending = useSelector(state => state.auth.verification);
  const dispatch = useDispatch();
  const [notice, setNotice] = useState(pending);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setNotice(pending);
    setSeconds(pending ? pending.retryAfter || 60 : 0);
  }, [pending]);
  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  if (!pending || !notice) return null;

  const resend = async () => {
    if (busy || seconds > 0) return;
    setBusy(true);
    try {
      const user = getAuth().currentUser;
      if (!user || user.email !== pending.email) throw Object.assign(new Error('Sign in again'), { code: 'auth/user-token-expired' });
      const result = await requestEmailVerification(user, redirectUrl);
      setNotice({ ...pending, message: verificationNotice(result), failed: false });
      setSeconds(60);
    } catch (error) {
      setNotice({ ...pending, message: verificationFailureMessage(error), failed: true });
      setSeconds(Number(error.response?.data?.retryAfter) || 60);
    } finally { setBusy(false); }
  };

  return <section aria-label="E-postverifiering" style={{ padding: '1.5rem', marginBottom: '1.5rem', background: '#f0f7f7', border: '1px solid #c6e1e0', borderRadius: 12 }}>
    <Mail size={28} aria-hidden="true" style={{ color: '#16676b', marginBottom: 12 }} />
    <h2 style={{ fontSize: '1.25rem' }}>Bekräfta din e-postadress</h2>
    <p>Ditt konto finns kvar för <strong>{pending.email}</strong>. Du behöver inte registrera dig igen.</p>
    <p role={notice.failed ? 'alert' : 'status'}>{notice.message}</p>
    <button type="button" className="btn btn-primary w-100 mb-3" disabled={busy || seconds > 0} onClick={resend}>
      {busy ? 'Skickar…' : seconds > 0 ? `Skicka ny länk om ${seconds} s` : 'Skicka ny verifieringslänk'}
    </button>
    <Link to={authPageLink('/login', redirectUrl)} onClick={() => dispatch(clearAuthMessages())}>Jag har verifierat – logga in</Link>
    <p style={{ marginTop: 12, marginBottom: 0, fontSize: '0.875rem', color: '#526569' }}>Använd lösenordet du valde när du skapade kontot. Din företagsinbjudan följer med.</p>
  </section>;
}
